import CircleCheckedFilled from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import {
  Checkbox,
  Dialog,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import radioUnchecked from '../../assets/images/icons/radioUnchecked.svg'
import { HarborCsThemeProvider } from '../../utils/context/HarborCsThemeProvider'
import { useToken } from '../../utils/context/TokenContext'
import { NumberFormatCustom } from '../../utils/functions'
import GoldenButton from '../Common/Buttons/GoldenButton'
import RecaptchaWrapper from '../Form/RecaptchaWrapper'

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 9999,
  '& .MuiPaper-root': {
    padding: '35px 30px',
    boxShadow: '0px 3.8px 22.7px rgba(0, 0, 0, 0.07)',
    borderRadius: '23px',
    minWidth: 639,
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      minWidth: '90vw',
      padding: '23px 15px',
      maxHeight: '75vh',
    },
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: 20,
  lineHeight: '32px',
  marginBottom: 5,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    lineHeight: '24px',
    marginBottom: 8,
  },
}))

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  lineHeight: '24px',
  marginBottom: 24,
}))

const ContentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}))

const InlineInputs = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 19,
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 16,
  },
}))

const CloseIconWrapper = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 20,
  right: 16,
}))

const StyledCheckboxLabel = styled('span')(({ theme }) => ({
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '18px',
}))

const RADIO_UNCHECKED_ICON = <img src={radioUnchecked} alt="unchecked" />
const CIRCLE_CHECKED_ICON = <CircleCheckedFilled />
const PHONE_INPUT_PROPS = { inputComponent: NumberFormatCustom }
const BACKDROP_PROPS = { style: { backgroundColor: 'rgba(9, 42, 72, 0.95)' } }

const formValuesInitialState = {
  firstName: '',
  lastName: '',
  eMail: '',
  zipCode: '',
  daytimePhone: '',
  message: '',
  directShareHolder: false,
  institutionalInvestor: false,
  companyName: '',
  crdNumber: '',
}

const formErrorsInitialState = {
  firstNameError: '',
}

const getButtonStyle = (mobileView) => ({ minWidth: mobileView ? '100%' : 174 })

function RequestACallModal({ isOpen = false, onClose = () => {} }) {
  const [recaptcha, setRecaptcha] = useState('')
  const [isSubmitedSuccess, setIsSubmitedSuccess] = useState(false)
  const [formValues, setFormValues] = useState(formValuesInitialState)
  const [formErrors, setFormErrors] = useState(formErrorsInitialState)
  const { csrfToken, fetchCsrfToken } = useToken()
  const [token, setToken] = useState(csrfToken)
  const mobileView = useMediaQuery('(max-width: 500px)')

  const handleClose = useCallback(() => {
    onClose()
    setTimeout(() => {
      setIsSubmitedSuccess(false)
    }, 1000)
  }, [onClose])

  const handleValueChange = useCallback(
    (e) => {
      const { value, name, checked, type } = e.target
      const newValues = { ...formValues }
      if (type === 'checkbox') {
        newValues[name] = checked
        const otherName =
          name === 'directShareHolder'
            ? 'institutionalInvestor'
            : 'directShareHolder'
        newValues[otherName] = false
      } else {
        newValues[name] = value
      }
      setFormValues(newValues)
    },
    [formValues]
  )

  const handleServerResponse = useCallback((res) => {
    const { status, errors } = res || {}
    if (status === 'success') {
      setFormValues(formValuesInitialState)
      setIsSubmitedSuccess(true)
      setFormErrors(formErrorsInitialState)
    } else {
      setFormErrors(errors || {})
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    const formAction = '/api/request-a-call'
    const body = { ...formValues, recaptcha }
    try {
      if (!csrfToken) {
        await fetchCsrfToken()
        setToken(csrfToken)
      }
      const resp = await fetch(formAction, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': token,
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      })
      if (!resp.ok) throw new Error('Failed to submit the form.')
      const jsonRes = await resp.json()
      handleServerResponse(jsonRes)
    } catch {
      setFormErrors((prev) => ({
        ...prev,
        generalError: 'An error occurred. Please try again.',
      }))
    }
  }, [
    formValues,
    recaptcha,
    csrfToken,
    token,
    fetchCsrfToken,
    handleServerResponse,
  ])

  const handleRecaptchaChange = useCallback((val) => {
    setRecaptcha(val)
  }, [])

  const handleRecaptchaExpire = useCallback(() => {
    setRecaptcha('')
  }, [])

  const buttonStyle = getButtonStyle(mobileView)

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleClose}
      BackdropProps={BACKDROP_PROPS}
    >
      <HarborCsThemeProvider>
        <CloseIconWrapper onClick={handleClose}>
          <CloseIcon />
        </CloseIconWrapper>
        <Title>{isSubmitedSuccess ? 'Thank You!' : 'Request a call'}</Title>
        <SubTitle>
          {isSubmitedSuccess
            ? 'We have received your request and will be in touch.'
            : 'Please Note: Information sent through e-mail is neither encrypted nor secure.'}
        </SubTitle>
        {!isSubmitedSuccess && (
          <ContentContainer>
            <InlineInputs>
              <TextField
                variant="outlined"
                placeholder="First Name"
                value={formValues.firstName}
                name="firstName"
                onChange={handleValueChange}
                error={!!formErrors.firstNameError}
                helperText={formErrors.firstNameError}
                fullWidth
              />
              <TextField
                variant="outlined"
                placeholder="Last Name"
                value={formValues.lastName}
                name="lastName"
                onChange={handleValueChange}
                error={!!formErrors.lastNameError}
                helperText={formErrors.lastNameError}
                fullWidth
              />
            </InlineInputs>
            <TextField
              variant="outlined"
              placeholder="Email"
              value={formValues.eMail}
              name="eMail"
              onChange={handleValueChange}
              error={!!formErrors.emailError}
              helperText={formErrors.emailError}
              fullWidth
            />
            <InlineInputs>
              <TextField
                variant="outlined"
                placeholder="ZIP Code"
                value={formValues.zipCode}
                name="zipCode"
                onChange={handleValueChange}
                fullWidth
              />
              <TextField
                variant="outlined"
                placeholder="Phone Number"
                value={formValues.daytimePhone}
                name="daytimePhone"
                onChange={handleValueChange}
                InputProps={PHONE_INPUT_PROPS}
                error={!!formErrors.phoneNumberError}
                helperText={formErrors.phoneNumberError}
                fullWidth
              />
            </InlineInputs>
            <TextField
              multiline
              rows={!mobileView ? 6 : 10}
              variant="outlined"
              placeholder="Message"
              value={formValues.message}
              name="message"
              onChange={handleValueChange}
              fullWidth
            />
            <InlineInputs>
              <Checkbox
                icon={RADIO_UNCHECKED_ICON}
                checkedIcon={CIRCLE_CHECKED_ICON}
                checked={formValues.directShareHolder}
                name="directShareHolder"
                onChange={handleValueChange}
              />
              <StyledCheckboxLabel>Direct shareholder</StyledCheckboxLabel>
              <Checkbox
                icon={RADIO_UNCHECKED_ICON}
                checkedIcon={CIRCLE_CHECKED_ICON}
                checked={formValues.institutionalInvestor}
                name="institutionalInvestor"
                onChange={handleValueChange}
              />
              <StyledCheckboxLabel>Institutional investor</StyledCheckboxLabel>
            </InlineInputs>
            {formValues.institutionalInvestor && (
              <>
                <TextField
                  variant="outlined"
                  placeholder="Company Name (Optional)"
                  value={formValues.companyName}
                  name="companyName"
                  onChange={handleValueChange}
                  fullWidth
                />
                <TextField
                  variant="outlined"
                  placeholder="CRD# (Optional)"
                  value={formValues.crdNumber}
                  name="crdNumber"
                  onChange={handleValueChange}
                  fullWidth
                />
              </>
            )}
            <InlineInputs>
              <RecaptchaWrapper
                formId="RequestACallModal"
                gatsbyRecaptchaKey={process.env.GATSBY_RECAPTCHA_KEY || ''}
                onRecaptchaChange={handleRecaptchaChange}
                onRecaptchaExpire={handleRecaptchaExpire}
                formSubmitted={isSubmitedSuccess}
              />
              <GoldenButton
                customStyle={buttonStyle}
                handleClick={handleSubmit}
              />
            </InlineInputs>
            {formErrors.generalError && (
              <FormHelperText error>{formErrors.generalError}</FormHelperText>
            )}
          </ContentContainer>
        )}
      </HarborCsThemeProvider>
    </StyledDialog>
  )
}

RequestACallModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
}

export default RequestACallModal
